import { Icon } from "@iconify/react/dist/iconify.js";
import React from "react";
import { Link } from "react-router-dom";

/**
 * Renderiza un submenú desplegable dentro de un menú de navegación.
 *
 * El componente muestra un botón que permite expandir o contraer una lista de
 * enlaces secundarios. Se utiliza para agrupar opciones relacionadas dentro
 * del Header, manteniendo una estructura y comportamiento consistentes.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.visible - Indica si el menú principal se encuentra visible para aplicar la clase correspondiente.
 * @param {boolean} props.open - Indica si el submenú se encuentra expandido.
 * @param {Function} props.onToggle - Función que alterna el estado de apertura del submenú.
 * @param {string} props.icon - Identificador del icono de Iconify que acompaña al botón.
 * @param {string} props.label - Texto que se muestra como título del submenú.
 * @param {Array<Object>} props.items - Lista de opciones que se renderizarán dentro del submenú.
 * @param {Function} props.onItemClick - Función que se ejecuta al seleccionar una opción del submenú.
 * @param {string} props.idPrefix - Prefijo utilizado para generar claves únicas para los elementos de la lista.
 *
 * @returns {JSX.Element} SubMenu
 */
export const SubMenu = ({
    visible,
    open,
    onToggle,
    icon,
    label,
    items,
    onItemClick,
    idPrefix,
}) => (
    <li
        className={`${visible ? "activeMode" : "inactiveMode"}`}
        role="none"
    >
        <button
            type="button"
            onClick={onToggle}
            className={`link-principal w-100 focusable-red ${
                open ? "link-principal-active" : ""
            }`}
            role="menuitem"
            aria-expanded={open}
        >
            <Icon icon={icon} width={20} className="mr-3" />

            {label}

            <Icon
                icon="material-symbols:keyboard-arrow-down-rounded"
                className="icon-down ml-2"
                width={20}
            />
        </button>

        <ul className={`dropdown-menu ${open ? "open" : ""}`}>
            {items.map((item, index) => (
                <li
                    key={`${idPrefix}-${item.id || index}`}
                    className="dropdown-item"
                    style={{
                        transitionDelay: open
                            ? `${index * 50}ms`
                            : "0ms",
                    }}
                >
                    {item.external ? (
                        <a
                            href={item.link}
                            target={item.target || "_blank"}
                            rel="noopener noreferrer"
                            className="focusable-red"
                            onClick={onItemClick}
                        >
                            {item.name}
                        </a>
                    ) : (
                        <Link
                            to={item.link}
                            className="focusable-red"
                            onClick={onItemClick}
                        >
                            {item.name}
                        </Link>
                    )}
                </li>
            ))}
        </ul>
    </li>
);